import { useEffect, useState } from "react";
import { FileX } from "lucide-react";
import { EmptyState } from "@strivelabs/ui-blocks";
import { HtmlIframePreview } from "@/components/reports/HtmlIframePreview";
import { ReportCanvasSplash } from "@/components/report-canvas";

export interface ArtifactPageContentProps {
  title: string;
  /** Presigned URL serving the version on screen. */
  contentSrc?: string;
  /** Version being shown, which keys the iframe so a reload actually happens. */
  version?: number;
  /** True until the artifact's details have ever loaded. */
  isPreparing: boolean;
  /** True when the artifact is gone (deleted, or never belonged to us). */
  isMissing: boolean;
}

/** The version on screen, and the URL it was signed for. */
interface ShownPage {
  version?: number;
  src: string;
}

/**
 * The page itself, without the chrome around it.
 *
 * Apart from any one panel because two hosts show a page: the chat canvas, and
 * the docked details panel a task opens one in. Both need the same three
 * states — waiting, gone, and the document — and the same rule about when the
 * document is allowed to reload.
 */
export function ArtifactPageContent({
  title,
  contentSrc,
  version,
  isPreparing,
  isMissing,
}: ArtifactPageContentProps) {
  // Every read of an artifact signs a fresh URL, so the string changes even
  // when the bytes have not. The document is pinned to the version on screen:
  // a refetch that leaves the version alone must not reload the page and throw
  // away what the person had typed into it.
  const [shown, setShown] = useState<ShownPage | null>(null);

  useEffect(() => {
    if (contentSrc === undefined) return;
    setShown((current) =>
      current !== null && current.version === version ? current : { version, src: contentSrc },
    );
  }, [contentSrc, version]);

  const page = isMissing || isPreparing ? null : shown;

  if (isMissing) {
    return (
      <div className="flex h-full w-full items-center justify-center p-6">
        <EmptyState
          icon={FileX}
          title="Artifact not found"
          description="This artifact may have been deleted or you might not have access."
        />
      </div>
    );
  }

  if (page === null) {
    return (
      <ReportCanvasSplash
        title="Opening your artifact…"
        description="Almost there — preparing the preview."
      />
    );
  }

  return (
    <HtmlIframePreview
      // Keyed by the version, so a rewritten artifact reloads rather than
      // leaving the previous document on screen, while a re-signed URL for the
      // version already on screen changes nothing.
      key={page.version ?? "current"}
      src={page.src}
      title={title}
      className="h-full"
    />
  );
}

/** What the header adds under an artifact's name, when there is anything to add. */
export function artifactSubtitle(version?: number, latestVersion?: number): string | undefined {
  // An artifact the agent has rewritten since this card was posted still opens
  // on the version the card named, so the header says which one that is. On the
  // current version there is nothing to say: the panel is the same panel a task
  // opens the same page in, and that one carries the name alone.
  return version !== undefined && latestVersion !== undefined && version < latestVersion
    ? `v${version} of ${latestVersion}`
    : undefined;
}
